<template>
    <div>
        <div>
            <div class="text-h5 q-mb-sm">
                {{ $t('Downtime Report') }}
            </div>
            <div class="row q-col-gutter-md">
                <date-time-picker
                    v-model="dateTimePicker.start"
                    :label="$t('Start')"
                    class="col-md-4"
                    style="max-width: 250px"
                    dense
                ></date-time-picker>
                <date-time-picker
                    v-model="dateTimePicker.end"
                    :label="$t('End')"
                    class="col-md-4"
                    style="max-width: 250px"
                    dense
                ></date-time-picker>
            </div>
        </div>

        <!-- Modes -->
        <div class="row q-mb-sm q-col-gutter-sm">
            <q-select
                v-model="yAxis.selected"
                :label="$t('Y-axis')"
                :options="yAxis.options"
                color="accent"
                outlined
                dense
            ></q-select>
            <q-radio
                v-model="selectProductMode"
                val="all"
                :label="$t('All Products')"
                color="accent"
            />
            <q-radio
                v-model="selectProductMode"
                val="custom"
                :label="$t('Custom')"
                color="accent"
            />
        </div>

        <div class="row">
            <!-- pareto chart -->
            <div
                :class="
                    selectProductMode === 'all'
                        ? 'col-xs-12'
                        : 'col-md-8 col-xs-12'
                "
            >
                <q-card bordered>
                    <v-chart
                        :option="chartOptions"
                        autoresize
                        style="height: 450px; width: 100%"
                    />
                </q-card>
            </div>

            <!-- Product selection panel -->
            <div
                v-if="selectProductMode === 'custom'"
                class="col-md col-xs-12 q-ml-sm"
            >
                <q-card
                    bordered
                    class="q-pa-md"
                    style="height: 450px; overflow-y: auto"
                >
                    <div class="row q-mb-md">
                        <table-select
                            v-model="productSelect.selected"
                            :label="productSelect.label"
                            :baseURL="productSelect.url"
                            :dialogTitle="productSelect.dialogTitle"
                            :searchLabel="productSelect.searchLabel"
                            :columns="productSelect.columns"
                            :dense="true"
                            :row-key="'product'"
                            class="q-mb-md vertical-middle"
                            style="margin: auto"
                        ></table-select>
                    </div>
                    <div
                        v-for="product in productSelect.selected"
                        :key="product.id"
                    >
                        <q-chip
                            @remove="removeProduct(product.id)"
                            :label="product.name"
                            class="q-pa-sm q-ma-xs"
                            color="secondary"
                            text-color="white"
                            dense
                            removable
                        ></q-chip>
                    </div>
                </q-card>
            </div>
        </div>

        <!-- Data table -->
        <q-table
            :rows="tableData"
            :columns="table.columns"
            :no-data-label="
                $t('No downtime data found for the given date range.')
            "
            class="q-mt-md"
        >
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary text-white"
                    >
                        {{ col.label }}
                    </q-th>
                </q-tr>
            </template>

            <!-- Action column -->
            <template v-slot:body-cell-action="props">
                <q-td :props="props">
                    <q-btn
                        @click="showDetailDialog(props.row)"
                        icon="far fa-search-plus"
                        color="secondary"
                        class="table__action-button"
                    />
                </q-td>
            </template>
        </q-table>

        <!-- Downtime event detail dialog  -->
        <q-dialog v-model="detailDialog.show" persistent>
            <q-card :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'">
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <q-space />

                    <q-btn
                        @click="closeDetailDialog"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Downtime Event Table -->
                <q-card-section>
                    <q-table
                        @request="onRequest"
                        v-model:pagination="detailDialog.table.pagination"
                        :rows="detailDialog.table.data"
                        :columns="detailDialog.table.columns"
                        :rows-per-page-options="[30]"
                        :loading="detailDialog.table.isLoading"
                        :no-data-label="$t('No downtime event available')"
                        table-style="height: 400px;"
                        row-key="id"
                        binary-state-sort
                        flat
                        bordered
                        ><template v-slot:top>
                            <div class="row">
                                <!-- table title (first row) -->
                                <div class="col-12 text-h5 q-mb-md">
                                    {{ $t('Details') }}
                                </div>
                                <div class="col-12 text-grey-9">
                                    {{ detailDialog.subtitle }}
                                    between
                                    {{
                                        formatDate(
                                            dateTimePicker.start,
                                            userLocale
                                        )
                                    }}
                                    and
                                    {{
                                        formatDate(
                                            dateTimePicker.end,
                                            userLocale
                                        )
                                    }}
                                </div>
                            </div>
                        </template>
                        <!-- start and end column -->
                        <template v-slot:body-cell-start="props">
                            <q-td :props="props">
                                {{ formatDate(props.row.start, userLocale) }}
                            </q-td>
                        </template>
                        <template v-slot:body-cell-end="props">
                            <q-td :props="props">
                                {{ formatDate(props.row.end, userLocale) }}
                            </q-td>
                        </template>
                        <!-- Description column -->
                        <template v-slot:body-cell-description="props">
                            <q-td :props="props">
                                {{ truncateText(props.row.description) }}
                            </q-td>
                        </template>
                        <!-- update button column -->
                        <template v-slot:body-cell-action="props">
                            <q-td :props="props">
                                <q-btn
                                    color="primary"
                                    icon="fas fa-edit"
                                    @click="update(props.row)"
                                    class="table__action-button"
                                ></q-btn>
                            </q-td>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section class="q-px-lg float-right">
                    <q-btn
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        @click="closeDetailDialog"
                        outline
                        >{{ $t('Cancel') }}</q-btn
                    >
                    <q-btn color="primary" @click="closeDetailDialog">{{
                        $t('Confirm')
                    }}</q-btn>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import TableSelect from '@components/TableSelect.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import { mapGetters } from 'vuex';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';

use([CanvasRenderer, LineChart, BarChart, TooltipComponent]);

const MAX_LABEL_LENGTH = 25;
const PARETO_TOP = 15;

export default {
    name: 'DowntimeReport',
    components: { DateTimePicker, TableSelect, VChart },
    mixins: [UpdateURL],
    props: {
        assetId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        /**
         * Initializes date time picker on creation. Updates URL parameter on changes.
         */
        dateTimePicker: {
            immediate: true,
            deep: true,
            async handler(picker) {
                if (!(picker.start && picker.end)) {
                    // initialize date picker to be the current week
                    picker.start = new Date(
                        new Date().getTime() - 1000 * 60 * 60 * 24 * 7
                    ).toISOString();
                    picker.end = new Date().toISOString();
                }
                const url = `assets/${this.assetId}/reports/downtime/?start=${picker.start}&end=${picker.end}`;
                this.report = await this.getDowntimeReport(url);
            }
        },
        /**
         * Updates payload to send server to generate report.
         */
        'productSelect.selected': {
            deep: true,
            async handler(selection) {
                // ids of the products selected
                const productIds = selection.map(product => product.id);
                // fetch data
                const url = `assets/${this.assetId}/reports/downtime/?start=${this.dateTimePicker.start}&end=${this.dateTimePicker.end}`;
                this.report = await this.getDowntimeReport(url, {
                    products: productIds
                });
            }
        },
        /**
         * Refetch results when the user goes back to mode 'all'.
         */
        async selectProductMode() {
            if (this.selectProductMode === 'all') {
                const url = `assets/${this.assetId}/reports/downtime/?start=${this.dateTimePicker.start}&end=${this.dateTimePicker.end}`;
                this.report = await this.getDowntimeReport(url);
                this.loadTableData(this.report);
            }
        }
    },
    data() {
        return {
            report: [],

            dateTimePicker: {
                start: null,
                end: null
            },

            table: {
                columns: [
                    {
                        name: 'name',
                        field: 'name',
                        label: this.$t('Name'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'code',
                        field: 'code',
                        label: this.$t('SKU'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'count',
                        field: 'count',
                        label: this.$t('Events'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'total_downtime',
                        field: 'total_downtime',
                        label: this.$t('Total downtime (min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'action',
                        field: 'action',
                        label: this.$t('Action'),
                        align: 'left'
                    }
                ]
            },

            // select product mode (values: 'all' or 'custom')
            selectProductMode: 'all',

            // product selector
            productSelect: {
                selected: [],
                url: `/assets/${this.assetId}/products/`,
                label: this.$t('Select products'),
                dialogTitle: this.$t('Select products'),
                searchLabel: this.$t('Search product'),
                columns: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('SKU'),
                        sortable: true
                    }
                ]
            },

            // y-axis options
            yAxis: {
                // v-model
                selected: {
                    label: this.$t('Occurence'),
                    value: 'occurence'
                },
                options: [
                    {
                        label: this.$t('Occurence'),
                        value: 'occurence'
                    },
                    {
                        label: this.$t('Duration'),
                        value: 'duration'
                    }
                ]
            },

            // event detail dialog
            detailDialog: {
                show: false,
                subtitle: '',
                causeId: null,
                // event table
                table: {
                    data: [],
                    columns: [
                        {
                            name: 'start',
                            field: 'start',
                            label: this.$t('Start Time'),
                            align: 'left',
                            sortable: true
                        },
                        {
                            name: 'end',
                            field: 'end',
                            label: this.$t('End Time'),
                            align: 'left',
                            sortable: true
                        },
                        {
                            name: 'description',
                            field: 'description',
                            label: this.$t('Description'),
                            align: 'left'
                        },
                        {
                            name: 'action',
                            field: 'action',
                            label: this.$t('Action'),
                            align: 'left'
                        }
                    ],
                    pagination: {
                        sortBy: '',
                        descending: false,
                        page: 1,
                        rowsPerPage: 30,
                        rowsNumber: null
                    },
                    isLoading: false
                }
            }
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get asset object.
         * @return {Object}
         */
        asset() {
            return this.getAsset(this.assetId);
        },
        /**
         *
         */
        tableData() {
            return [...this.report];
        },
        /**
         * Build the Pareto chart by occurence
         */
        occurenceData() {
            return this.buildParetoData('count');
        },
        /**
         * Build the Pareto chart by duration
         */
        durationData() {
            return this.buildParetoData('total_downtime');
        },
        paretoMode() {
            return this.yAxis.selected.value;
        },
        /**
         * Chart options
         */
        chartOptions() {
            let data = {
                x: [],
                y: [],
                cumsum: []
            };
            // build the pareto data
            if (this.paretoMode === 'occurence') {
                data = this.occurenceData;
            } else {
                data = this.durationData;
            }
            // calculate the max value for the primary y axis
            let yAxisMax = 0;
            let yAxisInterval = 10;
            if (data['y'].length >= 0) {
                yAxisMax = data['y'][0] * 1.25;
                yAxisInterval = yAxisMax / 10;
            }

            // build the echarts options
            return {
                tooltip: {
                    show: true,
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                        crossStyle: {
                            color: '#999'
                        }
                    }
                },
                grid: {
                    top: 75,
                    left: 100,
                    height: 300
                },
                toolbox: {
                    feature: {
                        saveAsImage: { show: true }
                    },
                    top: 0,
                    height: 100
                },
                xAxis: [
                    {
                        type: 'category',
                        data: data['x'],
                        axisPointer: {
                            show: true,
                            type: 'shadow'
                        },
                        axisLabel: {
                            interval: 0,
                            rotate: 25,
                            formatter: function (value, index) {
                                // if the xlabel is too long, use ellipsis
                                if (value.length <= MAX_LABEL_LENGTH) {
                                    return value;
                                } else {
                                    return (
                                        value.substr(0, MAX_LABEL_LENGTH - 3) +
                                        '...'
                                    );
                                }
                            }
                        },
                        splitLine: {
                            show: false
                        }
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        name: this.yAxis.selected.label,
                        min: 0,
                        max: yAxisMax,
                        interval: yAxisInterval,
                        axisLabel: {
                            formatter: function (value, index) {
                                // if the xlabel is too long, use ellipsis
                                return value.toFixed(1);
                            }
                        }
                    },
                    {
                        type: 'value',
                        name: this.$t('Cumulative Percentage'),
                        min: 0,
                        max: 100,
                        interval: 10,
                        axisLabel: {
                            formatter: '{value}%'
                        }
                    }
                ],
                series: [
                    {
                        name: this.yAxis.selected.label,
                        type: 'bar',
                        data: data['y']
                    },
                    {
                        name: 'Cumsum',
                        type: 'line',
                        yAxisIndex: 1,
                        data: data['cumsum']
                    }
                ]
            };
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        formatDate(datetime, locale) {
            return getLocaleFormat(datetime, locale);
        },
        /**
         * Truncate the text when the length is larger than 30
         * @return {String} the text after truncation
         */
        truncateText(text) {
            if (text && text.length >= 80) {
                return text.slice(0, 80) + '...';
            } else {
                return text;
            }
        },
        /**
         * Get downtime report.
         * @param {String} url
         */
        async getDowntimeReport(url, payload = {}) {
            // start the loading animation
            this.beginLoading(`${this.$t('Building report...')}`);
            // fetch data
            let report = [];
            await this.$axios
                .post(url, payload)
                .then(response => {
                    const data = response.data;

                    // format the data
                    for (const row of data) {
                        report.push({
                            cause_id: row['cause_id'],
                            name: row['cause_name'],
                            code: row['cause_code'],
                            count: row['count'],
                            total_downtime: row['total_downtime']
                        });
                    }
                })
                .catch(error => {
                    console.error('error: ', error);
                })
                .finally(() => {
                    this.stopLoading();
                });
            return report;
        },
        /**
         * Build Pareto data, using a specific property to rank the results
         */
        buildParetoData(orderingProperty) {
            let orderedData = [...this.report].sort((a, b) => {
                return -a[orderingProperty] + b[orderingProperty];
            });

            // select the top N-1
            let labels = [];
            let values = [];
            let i = 0;
            for (; i < PARETO_TOP - 1 && i < orderedData.length; i++) {
                labels.push(orderedData[i]['name']);
                values.push(orderedData[i][orderingProperty]);
            }
            // aggregate the remaining items
            if (i < orderedData.length) {
                let remainingValues = 0;
                for (let i = PARETO_TOP - 1; i < orderedData.length; i++) {
                    remainingValues += orderedData[i][orderingProperty];
                }
                values.push(remainingValues);
                labels.push(this.$t('Other'));
            }
            // calculate the cumulative sum
            const total = values.reduce((a, b) => a + b, 0);
            const cumulativeSummer = (
                sum => value =>
                    (sum += (value * 100) / total)
            )(0);
            const cumsums = values
                .map(cumulativeSummer)
                .map(sum => sum.toFixed(2));
            return {
                x: labels,
                y: values,
                cumsum: cumsums
            };
        },
        /**
         * Remove a product given its ID in the list of product selected.
         * @param {Number} productId
         */
        removeProduct(productId) {
            // find product index
            const index = this.productSelect.selected.findIndex(
                product => product.id === productId
            );
            // remove product
            this.productSelect.selected.splice(index, 1);
        },

        /**************
         * Detail dialog related methods *
         *************/
        /**
         * Show the detail dialog.
         * @param {Object} row - Cause from table row.
         */
        showDetailDialog(row) {
            // set up the fields of the detail dialog
            this.detailDialog.subtitle = `Downtime events for "${this.asset.name}" due to "${row.name}" that happened`;
            this.detailDialog.causeId = row.cause_id;
            // show the dialog
            this.detailDialog.show = true;
            // load event list
            this.onRequest({
                pagination: this.detailDialog.table.pagination
            });
        },
        /**
         * Close the detail dialog.
         */
        closeDetailDialog() {
            this.detailDialog.show = false;
            this.detailDialog.causeId = null;
            this.detailDialog.subtitle = '';
        },
        /**
         * Update the downtime event and route to the event detail page.
         * @param {Object} row - Downtime event from table row.
         */
        update(row) {
            // route to event detail page
            // and pass parameters
            this.$router.push({
                name: 'event_update',
                params: {
                    eventId: row.id,
                    product: row.product,
                    cause: row.cause,
                    asset: row.asset,
                    start: row.start,
                    end: row.end,
                    description: row.description
                }
            });
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         */
        async onRequest(props) {
            // begin loading
            this.detailDialog.table.isLoading = true;
            // clear existing data
            this.detailDialog.table.data = [];

            // update pagination object
            this.detailDialog.table.pagination = props.pagination;
            // get current pagination state attributes
            const { descending, page, sortBy } = props.pagination;
            // prepare the url params
            let urlParams = {
                asset: this.assetId,
                cause: this.detailDialog.causeId,
                ordering: descending ? `-${sortBy}` : `${sortBy}`,
                page: page,
                start__lte: this.dateTimePicker.end,
                end__gte: this.dateTimePicker.start
            };
            // update URL
            const url = this.updateURL({
                baseURL: 'events/',
                params: urlParams
            });
            const errorMessage = this.$t(
                'Failed to load events. Please try again later'
            );
            // send GET HTTPS request
            await this.$axios
                .get(url)
                .then(response => {
                    // push data
                    this.detailDialog.table.data.push(...response.data.results);
                    // update total events count
                    this.detailDialog.table.pagination.rowsNumber =
                        response.data.count;
                })
                .catch(error => {
                    console.error(error);
                    this.$notifyError(errorMessage, error);
                })
                .finally(() => {
                    this.detailDialog.table.isLoading = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 50%;
    max-width: none;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}
</style>
